<template>
  <Teleport to="body">
    <!-- Overlay -->
    <div
      class="bottom-sheet__overlay"
      :class="{
        'bottom-sheet__overlay--open': isOpen,
        'bottom-sheet__overlay--elevated': elevated && !superElevated,
        'bottom-sheet__overlay--super-elevated': superElevated,
      }"
      @click="$emit('update:modelValue', false)"
    />

    <!-- Bottom Sheet -->
    <div
      class="bottom-sheet"
      :class="{
        'bottom-sheet--open': isOpen,
        'bottom-sheet--elevated': elevated && !superElevated,
        'bottom-sheet--super-elevated': superElevated,
        'bottom-sheet--full-height': fullHeight,
        'bottom-sheet--no-padding': noPadding,
      }"
    >
      <div class="bottom-sheet__header">
        <div class="bottom-sheet__title-container">
          <div v-if="title || icon" class="bottom-sheet__title-wrapper">
            <Icon v-if="icon" :name="icon" :size="20" class="bottom-sheet__icon" />
            <h3 class="bottom-sheet__title">{{ title }}</h3>
            <p v-if="subtitle && !$slots.subtitle" class="bottom-sheet__subtitle">{{ subtitle }}</p>
          </div>
          <div v-if="$slots.subtitle" class="bottom-sheet__subtitle-below">
            <slot name="subtitle" />
          </div>
        </div>
        <button 
          class="bottom-sheet__close"
          @click="$emit('update:modelValue', false)"
        >
          <Icon name="close" :size="24" />
        </button>
      </div>
      
      <div class="bottom-sheet__content">
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import Icon from '@/components/icons/Icon.vue'

const props = defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  icon?: string
  // elevated et superElevated sont exclusifs : superElevated l'emporte si les deux sont passés
  elevated?: boolean
  superElevated?: boolean
  fullHeight?: boolean
  noPadding?: boolean
}>()

defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'edit'): void
}>()

// Découplé de modelValue pour garantir la transition CSS même quand le parent
// monte le composant avec modelValue déjà à true (pattern v-if)
const isOpen = ref(false)

onMounted(() => {
  if (props.modelValue) nextTick(() => { isOpen.value = true })
})

watch(() => props.modelValue, (value) => {
  if (value) nextTick(() => { isOpen.value = true })
  else isOpen.value = false
})
</script>

<style scoped>
.bottom-sheet__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 800;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.bottom-sheet__overlay--open {
  opacity: 1;
  pointer-events: auto;
}

.bottom-sheet__overlay--elevated {
  z-index: 900;
}

.bottom-sheet__overlay--super-elevated {
  z-index: 1000;
}

.bottom-sheet {
  position: fixed;
  bottom: -100%;
  left: 0;
  width: 100%;
  background: white;
  border-radius: 16px 16px 0 0;
  z-index: 801;
  /* Fermée : masquée aussi pour les lecteurs d'écran et les tests (le contenu reste monté),
     la visibilité ne bascule qu'après la glissade de fermeture */
  visibility: hidden;
  transition: bottom 0.3s ease, visibility 0s linear 0.3s;
}

.bottom-sheet--elevated {
  z-index: 901;
}

.bottom-sheet--super-elevated {
  z-index: 1001;
}

.bottom-sheet--full-height {
  height: 92vh;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.bottom-sheet--full-height .bottom-sheet__content {
  flex: 1;
  max-height: none;
  min-height: 0;
}

.bottom-sheet--open {
  bottom: 0;
  visibility: visible;
  transition: bottom 0.3s ease, visibility 0s;
}

.bottom-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.bottom-sheet__title-container {
  display: flex;
  flex-direction: column;
  gap: 0px;
  flex: 1;
  min-width: 0;
}

.bottom-sheet__title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bottom-sheet__icon {
  color: var(--color-primary);
  flex-shrink: 0;
}

.bottom-sheet__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  font-family: Outfit;
}

.bottom-sheet__subtitle {
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-light);
  font-family: Outfit;
}

/* Sous le titre quand il y en a un, seul dans l'en-tête sinon (pastilles d'une réserve) */
.bottom-sheet__title-wrapper + .bottom-sheet__subtitle-below {
  margin-top: 6px;
}

.bottom-sheet__close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
}

.bottom-sheet__content {
  max-height: 80vh;
  overflow-y: auto;
  padding: 1rem 0.25rem;
}

.bottom-sheet--no-padding .bottom-sheet__content {
  padding: 0;
  overflow: hidden;
}
</style>
